"use client";

import { create } from "zustand";

import type { InstantAssistantMessage } from "@/types/assistant";

/**
 * Tiny store for the floating assistant overlay (v0 sketch).
 * Deliberately separate from useInstantAssistantStore — the existing
 * panel keeps its own state until the rethink lands a real wiring.
 *
 * v0 has no backend: the assistant reply is a local placeholder so we
 * can feel the surface. Real session/streaming wiring comes later.
 *
 * Voice wiring: state shape is in place so the future voice session
 * can plug in without a refactor — `voiceStatus` lifecycle, input
 * modality on each message, and `lastInputModality` so the eventual
 * backend prompt has the response-shape signal defined in
 * `docs/assistant-modalities.md`. Audio transport / STT / TTS are
 * intentionally NOT here — they belong to a separate session.
 */

export type OverlayMessageRole = "user" | "assistant";
export type InputModality = "text" | "voice";
export type VoiceStatus = "idle" | "starting" | "active" | "ending";

/**
 * Backend status events delivered via SSE — `thinking` while the LLM is
 * working, `slow_path` after ~15s, `queued` when rate-limited, `error`
 * on failure, `idle` when the turn is done. The overlay surfaces
 * thinking / slow_path as a quiet pulsing-dots indicator under the
 * thread; queued and error are placeholders today.
 */
export type OverlayStatusKind = "idle" | "thinking" | "slow_path" | "queued" | "error";

export type OverlayStatus = {
  kind: OverlayStatusKind;
  detail: string | null;
};

export type OverlayMessage = {
  id: string;
  role: OverlayMessageRole;
  text: string;
  /**
   * Which input modality produced this turn (or, for assistant turns,
   * which input modality preceded it). Future response-shape adaptation
   * keys off this field.
   */
  inputModality: InputModality;
  /**
   * True for optimistic, locally-created user messages that have been
   * sent to the backend but whose canonical server echo has not yet
   * arrived via SSE. Reconciliation in appendServerMessage replaces
   * the pending entry with the server version on match.
   */
  pending?: boolean;
};

type OverlayState = {
  open: boolean;
  draft: string;
  messages: OverlayMessage[];
  dragOffset: { x: number; y: number };
  size: { w: number; h: number };
  /**
   * Which input modality the user used most recently. Drives the response
   * shape contract from `docs/assistant-modalities.md`. Defaults to "text".
   */
  lastInputModality: InputModality;
  /**
   * Lifecycle of the voice session. `idle` is the resting state.
   * `starting` / `ending` are reserved for the real voice wiring;
   * v0 only flips between `idle` and `active`.
   */
  voiceStatus: VoiceStatus;
  /**
   * Backend assistant-session id. When null, the overlay is in *demo* mode
   * (seed thread + local placeholder reply). When set, it's *live* — the
   * provider has bootstrapped a real backend session and SSE stream is
   * delivering messages. send() consults this to decide whether to do a
   * local echo or hand off to the provider for a real POST.
   */
  sessionId: string | null;
  /**
   * Backend-driven turn status (live mode only). Drives the thinking
   * indicator under the message thread.
   */
  status: OverlayStatus;
  /**
   * Monotonic counter bumped by `newSession`. The provider keys its
   * scope-skip check on this so an explicit "new session" click forces
   * a fresh backend session even when (namespace, user) is unchanged.
   */
  sessionResetTick: number;
  open_: () => void;
  close_: () => void;
  toggle_: () => void;
  setDraft: (value: string) => void;
  send: () => void;
  reset: () => void;
  /**
   * User-triggered "start a new session". Clears the visible thread and
   * drops the live session id; the provider re-bootstraps a fresh
   * backend session on the next render.
   */
  newSession: () => void;
  setDragOffset: (offset: { x: number; y: number }) => void;
  resetDragOffset: () => void;
  setSize: (size: { w: number; h: number }) => void;
  /**
   * Begin a voice session. v0 only flips state — the real implementation
   * (mic capture, STT, audio playback, backend session creation) is owned
   * by the future voice session and plugs in here.
   */
  startVoice: () => void;
  endVoice: () => void;
  toggleVoice: () => void;
  /** Backend wire-up — invoked by the provider, not the UI. */
  setSessionId: (sessionId: string | null) => void;
  replaceMessages: (messages: OverlayMessage[]) => void;
  appendServerMessage: (message: InstantAssistantMessage) => void;
  /**
   * Provider registers a handler when in live mode. send() delegates to
   * it instead of doing the local placeholder echo. Cleared when the
   * provider tears down (logout, namespace change, unmount).
   */
  setSubmitHandler: (handler: ((content: string) => void) | null) => void;
  /** Drop the live session and reset to demo (seed thread + placeholder). */
  clearLiveSession: () => void;
  /** Set the backend-driven turn status. */
  setStatus: (status: OverlayStatus) => void;
};

export const OVERLAY_DEFAULT_SIZE = { w: 432, h: 560 };
export const OVERLAY_MIN_SIZE = { w: 320, h: 280 };

// Mock thread mirrors the Figma sketch (node 792-7838) and extends it with
// enough back-and-forth to exercise scrolling. Real wiring still on hold.
const SEED_MESSAGES: OverlayMessage[] = [
  {
    id: "seed-user-1",
    role: "user",
    inputModality: "text",
    text: "Why is auth flaking on staging?",
  },
  {
    id: "seed-assistant-1",
    role: "assistant",
    inputModality: "text",
    text:
      "The login service has been timing out on a third of requests since Tuesday. The pattern lines up with a deploy that changed the password validator.",
  },
  {
    id: "seed-user-2",
    role: "user",
    inputModality: "text",
    text: "Show me the change.",
  },
  {
    id: "seed-assistant-2",
    role: "assistant",
    inputModality: "text",
    text:
      "The current password validator filters characters using an outdated allow-list. This blocks legitimate symbols such as @, %, +, £, and several Unicode code points. As a result, the login service receives incomplete or modified passwords and treats them as invalid credentials.",
  },
  {
    id: "seed-user-3",
    role: "user",
    inputModality: "text",
    text: "How widely is the validator used?",
  },
  {
    id: "seed-assistant-3",
    role: "assistant",
    inputModality: "text",
    text:
      "Fourteen call sites across three services. Two of them already special-case `@` and `+` upstream, which means those paths weren't affected — that's why the breakage looks intermittent rather than total.",
  },
  {
    id: "seed-user-4",
    role: "user",
    inputModality: "text",
    text: "What's the fastest fix that doesn't regress the SSO flow?",
  },
  {
    id: "seed-assistant-4",
    role: "assistant",
    inputModality: "text",
    text:
      "Move the validator to a deny-list of literal control characters and stop touching the rest. SSO never relied on the allow-list — it pre-validates against its own provider's rules — so the blast radius is limited to the local login form.",
  },
  {
    id: "seed-user-5",
    role: "user",
    inputModality: "text",
    text: "Mark this — I think the same validator is also why the API key import has been rejecting half the keys we paste in.",
  },
  {
    id: "seed-assistant-5",
    role: "assistant",
    inputModality: "text",
    text: "Logged. Linked to the password validator note.",
  },
];

const PLACEHOLDER_REPLY =
  "I'm a placeholder. Real wiring comes later — for now this is the surface, not the substance.";

let messageCounter = 0;
const nextId = (prefix: string) => {
  messageCounter += 1;
  return `${prefix}-${Date.now()}-${messageCounter}`;
};

/**
 * Module-level ref for the live-mode submit handler. The provider sets it
 * on mount; send() reads it. We don't put the handler in state because it
 * isn't a value to render — keeping it out of the store avoids a
 * re-render every time it's set/cleared.
 */
let submitHandlerRef: ((content: string) => void) | null = null;

/**
 * Map a backend `InstantAssistantMessage` (role: operator | assistant |
 * system) to the overlay's OverlayMessage. System messages are ignored
 * for v0 — the overlay surface doesn't render them.
 */
export function fromServerMessage(message: InstantAssistantMessage): OverlayMessage | null {
  const role: OverlayMessageRole | null =
    message.role === "operator"
      ? "user"
      : message.role === "assistant"
        ? "assistant"
        : null;
  if (!role) return null;
  return {
    id: message.messageId,
    role,
    text: message.content,
    // Server doesn't yet distinguish input modality — assume text until
    // the voice pipeline lands and starts emitting it.
    inputModality: "text",
  };
}

export const useAssistantOverlay = create<OverlayState>((set, get) => ({
  open: false,
  draft: "",
  messages: SEED_MESSAGES,
  dragOffset: { x: 0, y: 0 },
  size: OVERLAY_DEFAULT_SIZE,
  lastInputModality: "text",
  voiceStatus: "idle",
  sessionId: null,
  status: { kind: "idle", detail: null },
  sessionResetTick: 0,
  open_: () => set({ open: true }),
  close_: () => set({ open: false }),
  toggle_: () => set((state) => ({ open: !state.open })),
  setDraft: (value) => set({ draft: value }),
  send: () => {
    const trimmed = get().draft.trim();
    if (!trimmed) return;
    const handler = submitHandlerRef;
    if (handler && get().sessionId) {
      // Live mode: hand off to the provider's submit handler. The
      // handler POSTs to /api/assistant/sessions/{id}/messages and
      // SSE delivers both the user echo and the assistant reply.
      //
      // Optimistic append: drop the user's message into the thread
      // immediately with a temp id + pending: true. When the SSE
      // delivers the server echo, appendServerMessage replaces this
      // entry by matching role + text (see reconciliation logic
      // below). Status flips to thinking right away so the indicator
      // appears without waiting for the backend.
      const optimisticUser: OverlayMessage = {
        id: nextId("temp-user"),
        role: "user",
        text: trimmed,
        inputModality: "text",
        pending: true,
      };
      set((state) => ({
        draft: "",
        lastInputModality: "text",
        status: { kind: "thinking", detail: null },
        messages: [...state.messages, optimisticUser],
      }));
      handler(trimmed);
      return;
    }
    // Demo mode: no backend. Append user + (after a short pause)
    // placeholder reply locally so the surface is feelable without a
    // session, including the thinking indicator pulse — keeps /preview/*
    // working without auth and gives the indicator something to show.
    const userMessage: OverlayMessage = {
      id: nextId("user"),
      role: "user",
      text: trimmed,
      inputModality: "text",
    };
    set((state) => ({
      draft: "",
      lastInputModality: "text",
      status: { kind: "thinking", detail: null },
      messages: [...state.messages, userMessage],
    }));
    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        const assistantMessage: OverlayMessage = {
          id: nextId("assistant"),
          role: "assistant",
          text: PLACEHOLDER_REPLY,
          inputModality: "text",
        };
        set((state) => ({
          status: { kind: "idle", detail: null },
          messages: [...state.messages, assistantMessage],
        }));
      }, 1400);
    }
  },
  reset: () =>
    set({
      messages: SEED_MESSAGES,
      draft: "",
      lastInputModality: "text",
      voiceStatus: "idle",
    }),
  newSession: () =>
    set((state) => ({
      sessionId: null,
      messages: [],
      draft: "",
      lastInputModality: "text",
      voiceStatus: "idle",
      status: { kind: "idle", detail: null },
      sessionResetTick: state.sessionResetTick + 1,
    })),
  setDragOffset: (offset) => set({ dragOffset: offset }),
  resetDragOffset: () => set({ dragOffset: { x: 0, y: 0 } }),
  setSize: (size) => set({ size }),
  // Voice wiring — state machine only. The future voice session replaces
  // these implementations with real mic capture, STT streaming, audio
  // playback, and backend session creation. The shape stays the same so
  // the swap is local.
  startVoice: () => {
    if (get().voiceStatus !== "idle") return;
    set({ voiceStatus: "active", lastInputModality: "voice" });
  },
  endVoice: () => {
    if (get().voiceStatus === "idle") return;
    set({ voiceStatus: "idle" });
  },
  toggleVoice: () => {
    const status = get().voiceStatus;
    if (status === "idle") {
      get().startVoice();
    } else {
      get().endVoice();
    }
  },
  setSessionId: (sessionId) => set({ sessionId }),
  replaceMessages: (messages) => set({ messages }),
  appendServerMessage: (serverMessage) => {
    const next = fromServerMessage(serverMessage);
    if (!next) return;
    set((state) => {
      // 1. Dedupe by id — same SSE event arriving twice (reconnect, etc.)
      //    just replaces the existing turn.
      const idxById = state.messages.findIndex((m) => m.id === next.id);
      if (idxById >= 0) {
        const updated = [...state.messages];
        updated[idxById] = next;
        return { messages: updated };
      }
      // 2. Reconcile optimistic user messages: find the oldest pending
      //    user turn whose text matches the server echo and swap in the
      //    server version (canonical id, server timestamp). This makes
      //    the optimistic-append flow seamless to the user.
      if (next.role === "user") {
        const idxPending = state.messages.findIndex(
          (m) => m.pending === true && m.role === "user" && m.text === next.text,
        );
        if (idxPending >= 0) {
          const updated = [...state.messages];
          updated[idxPending] = next;
          return { messages: updated };
        }
      }
      // 3. Otherwise it's a fresh turn — append.
      return { messages: [...state.messages, next] };
    });
  },
  setSubmitHandler: (handler) => {
    submitHandlerRef = handler;
  },
  clearLiveSession: () =>
    set({
      sessionId: null,
      messages: SEED_MESSAGES,
      draft: "",
      lastInputModality: "text",
      voiceStatus: "idle",
      status: { kind: "idle", detail: null },
    }),
  setStatus: (status) => set({ status }),
}));
